import axios from 'axios';
import { toast } from 'react-toastify';
import { configureConnection, isNoResponse, reportReachable, reportUnreachable } from './connection';
import { rememberReturnTo } from '../utils/returnTo';

// Where the API lives:
//  - explicit REACT_APP_API_URL wins (client/.env.local points LAN phones at the Mac)
//  - production builds (Railway) call the same origin that served the page
//  - local dev falls back to the dev server port
export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

// Origin for non-API assets like /uploads. Empty string = same origin.
export const SERVER_ORIGIN = API_BASE_URL.replace(/\/api$/, '');

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

configureConnection({ healthUrl: `${API_BASE_URL}/health` });

// How long a call may wait for an answer before it counts as no answer.
// There was no limit at all, and on a laptop a socket that died during sleep
// or a Wi-Fi switch left the request hanging for good: a spinner that never
// cleared, a page that never filled (owner, Oct 20 2026). Reads are quick;
// writes get longer because some do real work; the AI, export and bulk calls
// genuinely run a minute or more and must not be cut off.
const TIMEOUT_READ_MS = 30_000;
const TIMEOUT_WRITE_MS = 90_000;
const TIMEOUT_LONG_MS = 180_000;
const LONG_CALL = /\/(ai|export|import|geocode|normalize|generate|optimi[sz]e|plan-sheet|bulk|tidy|receipt|sync|run)\b/;
export function timeoutFor(url: string, method: string): number {
  if (LONG_CALL.test(url)) return TIMEOUT_LONG_MS;
  return method === 'GET' || method === 'HEAD' ? TIMEOUT_READ_MS : TIMEOUT_WRITE_MS;
}

// For the pages a customer opens from a link (quote, invoice, plans, referral,
// portal). No sign-in token, no read-only gate and no 401 → /login redirect:
// the link's own token is the credential, and bouncing a customer to the staff
// login page would be wrong. Same base URL as `api`.
export const publicApi = axios.create({ baseURL: API_BASE_URL });

// The read-only accounts ('viewer' in the admin app, 'crew_viewer' in the crew
// app) have every non-GET refused by server/middleware/auth.js. Mirror that one
// rule here so a stray control gives an honest answer instead of a raw 403, and
// so nothing is half-applied optimistically in the UI first. AuthProvider keeps
// this in step with the signed-in user.
let readOnlyAccount = false;
export function setApiReadOnly(value: boolean) { readOnlyAccount = value; }
export function isApiReadOnly() { return readOnlyAccount; }

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const method = String(config.method || 'get').toUpperCase();
    if (readOnlyAccount && method !== 'GET' && method !== 'HEAD') {
      toast.info('View-only account — nothing was changed', { toastId: 'read-only' });
      return Promise.reject(Object.assign(new Error('View-only account — changes are not allowed'), {
        config, isReadOnlyBlock: true,
      }));
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.timeout) config.timeout = timeoutFor(String(config.url || ''), method);
    // A photo is FormData, not JSON. Axios v1 sees this instance's default
    // Content-Type of application/json and quietly rewrites the whole upload as
    // {"photo":{}} — the image never leaves the phone and the server answers
    // "No photo provided". Clearing the header lets the browser set
    // multipart/form-data with its boundary, which is the only thing multer
    // can read. (Crew photo uploads were dead until 2026-09-09 because of this.)
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete (config.headers as any)['Content-Type'];
      delete (config.headers as any)['content-type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    reportReachable();
    // A manager's change is captured for the owner to approve, not applied now,
    // so the screen won't reflect it — say so, or it looks like nothing happened.
    if (response.status === 202 && response.data?.pending) {
      toast.info(response.data.message || 'Sent to the owner to approve', { autoClose: 5000 });
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || '';
    // An answer of any kind means the server is there; none at all means it
    // is not, and the banner + /health ping take it from here.
    if (error.response) reportReachable();
    else if (isNoResponse(error)) reportUnreachable();
    // 401 = not authenticated (no token or invalid/expired token) → redirect to login
    if (status === 401) {
      localStorage.removeItem('token');
      // A signed-out crew page (an open-shift link on a phone whose login ran out) is where they go back to.
      rememberReturnTo(window.location.pathname + window.location.search);
      window.location.href = '/login';
    }
    // 403 "Invalid or expired token" can still arrive from older server versions
    if (status === 403 && message === 'Invalid or expired token') {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Why a call failed, in words for a toast. The server's own message when it
 * sent one; otherwise the honest reason, never a bare "Failed" (owner, Oct 27
 * 2026: sorting a bank charge on Profit "says failed" and nothing more).
 *   - the view-only account: nothing is sent at all
 *   - no answer (timed out, network dropped, the API mid-deploy)
 *   - an answer with no message (Railway's own 502/503 page while restarting,
 *     or a page that is not JSON): the status, and what to do about it
 */
export function whyFailed(err: any, fallback = 'Something went wrong'): string {
  if (err?.isReadOnlyBlock) return 'View-only account — nothing was changed';
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (isNoResponse(err)) return 'No answer from the server — check the connection and try again';
  const status = Number(err?.response?.status);
  if (status === 502 || status === 503 || status === 504) return `The server is restarting (${status}) — try again in a minute`;
  if (status >= 400) return `${fallback} (the server answered ${status})`;
  return fallback;
}

export default api;
